// @flow
import React from 'react';
import { get, isNaN, isEmpty, map, forEach, filter, sumBy, omit, size } from 'lodash';
import {
  Modal, Input, InputMask,
  Toggle, DayPickerInput, ClickableList,
  Checkbox, SelectBox, Loading,
  FormattedText, Button, Icon, QuantityStepper,
} from '@elements';
import { FormFooterModal, PlatformSwitch } from '@blocks';
import { paymentMethods } from '@constants';
import type { FormElementData, ServicesState } from '@types';
import { withForm } from '@hocs';
import { withTranslation } from 'react-i18next';
import { createNsTranslator, fSufixTwoPoints, handleSubmitEvent, translateConstant, formatDate } from '@helpers/helpers';
import './PaymentFormModal.scss';
import classNames from 'classnames';
import ServiceService from '../../../../../services/ServiceService';


type FormErrors = { [field: string]: string };
type ServicesCheckList = { [key: number]: boolean };

type Props = {
  indebt?: boolean,
  financialBalance?: number,
  onClose: Function,
  onSave: Function,
  onDelete?: Function,
  title: string,
  onSetConfirmOnClose: Function,
  serviceTemplates?: ?ServicesState,
  servicesByQuantity?: boolean,
  formData: Object,
  touchedFields: Object,
  bindValidation: Function,
  handleFormChange: Function,
  formChange: Function,
  handleTouchField: Function,
  formEdited: Function,
  t: Function,
};

type State = {
  errors: FormErrors,
  services: ServicesCheckList,
  quantities: { [key: number | string]: string },
  invalidQuantities: { [key: number | string]: boolean },
  missingQuantityOf: ?string,
  isPriceChanged: boolean,
  installments: number,
  showInstallments: boolean,
  renderServices: boolean,
};

const servicesItems = (itens, inDebit?: ?boolean, debitValue: number, showHeading: boolean, _: Function, renderServices: boolean, servicesByQuantity: boolean) => [
  ...(showHeading ? [
    {
      type: 'heading',
      text: _('action/add_new_services'),
    },
  ] : []),
  ...(inDebit || !renderServices ? [] : map(itens, item => ({
    ...(servicesByQuantity && ServiceService.isTemplateByQuantity(item) ? { className: 'listItem byQuantityItem' } : {}),
    customRendererData: {
      type: 'clickItem',
      id: item.id,
      text: item.name,
      price: item.price,
      ...(servicesByQuantity && ServiceService.isTemplateByQuantity(item) ? { unit: item.unit } : {}),
    },
  }))),
  ...(!inDebit || !renderServices ? [] : [
    {
      customRendererData: {
        type: 'clickItem',
        text: _('label/debt_balance'),
        price: debitValue > 0 ? debitValue : debitValue * -1,
      },
    },
  ]),
];

const installmentsOptions = [
  { text: '2x', value: 2 },
  { text: '3x', value: 3 },
  { text: '4x', value: 4 },
  { text: '5x', value: 5 },
  { text: '6x', value: 6 },
  { text: '7x', value: 7 },
  { text: '8x', value: 8 },
  { text: '9x', value: 9 },
  { text: '10x', value: 10 },
  { text: '11x', value: 11 },
  { text: '12x', value: 12 },
];

class PaymentFormModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      errors: {},
      services: {},
      quantities: {},
      invalidQuantities: {},
      missingQuantityOf: null,
      isPriceChanged: false,
      installments: parseInt(get(this.props.formData, 'installments', 0) || 0, 10),
      showInstallments: parseInt(get(this.props.formData, 'installments', 0) || 0, 10) > 1,
      renderServices: true,
    };
    this.scroolContainer = React.createRef();
  }

  componentDidMount() {
    const { bindValidation } = this.props;
    bindValidation(this.handleValidation);
    handleSubmitEvent('#paymentFormModalSubmit', this.onSave);
  }

  renderServiceItem = (item: Object, key: string) => {
    if (item.unit) return this.renderQuantityServiceItem(item, key);

    return [
      <Checkbox
        key={`check-${key}`}
        name="price"
        label={item.text}
        onChange={this.handleToggleService(item.id)}
        isChecked={get(this.state, ['services', String(item.id)], false)}
      />,
      <span key={`span-${key}`}><FormattedText type="currency" text={item.price} /></span>,
    ];
  };

  // A template sold by quantity: the quantity, at the template's price per unit
  renderQuantityServiceItem = (item: Object, key: string) => {
    const { t } = this.props;
    const _ = createNsTranslator('financial', t);
    const isChecked = get(this.state, ['services', String(item.id)], false);
    const quantityText = get(this.state, ['quantities', String(item.id)], '1');
    const quantity = ServiceService.parseQuantity(quantityText);

    return [
      <div className="quantityItemName" key={`name-${key}`}>
        <Checkbox
          name="price"
          label={item.text}
          onChange={this.handleToggleService(item.id)}
          isChecked={isChecked}
        />
        <div className="quantityItemHint">{t('financial:label/per_unit', { unit: item.unit })}</div>
      </div>,
      <div
        className={classNames('quantityItemControls', { active: isChecked })}
        key={`controls-${key}`}
        onClick={isChecked ? this.stopRowClick : undefined}
        role="presentation"
      >
        {isChecked ? (
          <React.Fragment>
            <QuantityStepper
              value={quantityText}
              invalid={get(this.state, ['invalidQuantities', String(item.id)], false)}
              onChange={this.handleQuantityChange(item.id)}
              inputLabel={_('label/quantity', 'global')}
              decreaseLabel={_('action/decrease_quantity')}
              increaseLabel={_('action/increase_quantity')}
            />
            <span className="quantityItemUnit">
              {ServiceService.unitLabel(item.unit, quantity)} × <FormattedText type="currency" text={item.price} />
            </span>
            <span className="quantityItemTotal">
              =&nbsp;<FormattedText type="currency" text={ServiceService.lineTotal(quantity, item.price)} />
            </span>
          </React.Fragment>
        ) : (
          <FormattedText type="currency" text={item.price} />
        )}
      </div>,
    ];
  };

  render() {
    const {
      onClose, onDelete, title, serviceTemplates, indebt, financialBalance,
      handleFormChange, handleTouchField, formData,
    } = this.props;
    const {
      method, price, notes, withNotaFiscal, notaFiscalNumber, date, id,
    } = formData;

    const { errors, showInstallments } = this.state;

    const _ = createNsTranslator('financial', this.props.t);
    const translatedPaymentMethods = translateConstant(paymentMethods);

    const isLoading = get(serviceTemplates, 'isLoading', false);
    const serviceTemplatesData = get(serviceTemplates, 'data', {});

    const showHeading = !isEmpty(serviceTemplatesData) && parseFloat(financialBalance) >= 0;

    const installments = parseInt(get(formData, 'installments', 0) || 0, 10);

    const headingRenderer = (item: any, key: string, classes: string) => (
      <li key={key} className={classNames(classes, 'checkableHeading')} onClick={this.toggleRenderServices}>
        <div className="textContent">
          {item.text}
        </div>
        <div className="checkboxHolder">
          {(this.state.renderServices) && (
            <React.Fragment>{_('action/hide_list', 'global')}&nbsp;<Icon iconName="arrow-down" /></React.Fragment>
          )}

          {!(this.state.renderServices) && (
            <React.Fragment>{_('action/show_list', 'global')}&nbsp;<Icon iconName="arrow-left" /></React.Fragment>
          )}
        </div>
      </li>
    );

    const content = (
      <React.Fragment>
        {isLoading && <Loading />}
        {!isLoading &&
          <ClickableList
            items={servicesItems(serviceTemplatesData, indebt, parseFloat(financialBalance), showHeading, _, this.state.renderServices, !!this.props.servicesByQuantity)}
            customRenderer={this.renderServiceItem}
            customHeadingRenderer={headingRenderer}
            onListItemClick={this.handleClickService}
            className="checkboxList"
          />
        }
        {this.state.missingQuantityOf && (
          <p className="quantityError" role="alert">
            {this.props.t('financial:label/error/quantity_of', { name: this.state.missingQuantityOf })}
          </p>
        )}

        <form className="form">
          <div className="split">
            <InputMask
              type="currency"
              name="price"
              label={_('label/value', 'global', fSufixTwoPoints)}
              value={price}
              onChange={this.handlePriceChange}
              onBlur={handleTouchField}
              error={get(errors, 'price', null)}
              autofocus
            />
            <DayPickerInput
              label={_('label/date', 'global', fSufixTwoPoints)}
              name="date"
              date={date}
              onSelect={handleFormChange}
            />
          </div>
          <br />

          <SelectBox
            name="method"
            label={_('label/payment_method', null, fSufixTwoPoints)}
            options={translatedPaymentMethods}
            value={method}
            withoutBorder
            onChange={this.handleMethodChange}
            onBlur={handleTouchField}
          />
          {!id && showInstallments && (
            <div className="inputHolder">
              <Toggle
                name="installments"
                label={_('label/with_installments')}
                isChecked={installments > 1}
                onChange={this.handleSwitchInstallments}
                onBlur={handleTouchField}
              />
            </div>
          )}
          {!id && installments > 1 && showInstallments && (
            <SelectBox
              name="installments"
              label={_('label/installments', null, fSufixTwoPoints)}
              options={installmentsOptions}
              value={installments}
              withoutBorder
              onChange={handleFormChange}
              onBlur={handleTouchField}
            />
          )}

          <Input
            name="notes"
            label={method === 'check' ? _('label/check_number', null, fSufixTwoPoints) : _('label/notes', 'global', fSufixTwoPoints)}
            value={notes}
            onChange={handleFormChange}
            onBlur={handleTouchField}
          />

          <div className="inputHolder">
            <Toggle
              name="withNotaFiscal"
              label={_('label/with_invoice')}
              isChecked={withNotaFiscal}
              onChange={this.handleSwitch}
              onBlur={handleTouchField}
            />
          </div>

          <Input
            name="notaFiscalNumber"
            id="notaFiscalNumber"
            label={_('label/invoice_number', null, fSufixTwoPoints)}
            value={notaFiscalNumber}
            onChange={handleFormChange}
            onBlur={handleTouchField}
            className={withNotaFiscal ? '' : 'hiddenElement'}
          />

        </form>

      </React.Fragment>
    );

    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <Modal id="PaymentsFormModal" onClose={onClose} className="desktop">
            <div className="modalHeader">
              <span className="modalTitle">{title}</span>
            </div>

            <div className="modalGrid">
              <div className="modalContainer">
                <div className="modalContent" ref={this.scroolContainer} id="paymentFormModalSubmit">
                  <div>
                    {content}
                  </div>
                </div>
                <FormFooterModal
                  onCancel={onClose}
                  onDelete={onDelete}
                  onSave={this.onSave}
                />
              </div>

            </div>
          </Modal>
        )}
        mobile={() => (
          <Modal id="PaymentsFormModal" className="asView" onClose={onClose}>
            <div className="modalHeader">
              <Button onClick={onClose}>
                <Icon iconName="close" className="icon" />
              </Button>
              <span className="title">{title}</span>
              <Button className="primary" text={_('action/save', 'global')} onClick={this.onSave} />
            </div>
            <div className="modalGrid">
              <div className="modalContainer">
                <div className="modalContent" ref={this.scroolContainer}>
                  {content}
                  {onDelete && (
                    <div className="formDeleteAction">
                      <Button className="dangerOutline" text={_('action/delete_payment')} onClick={onDelete} />
                    </div>
                  )}
                </div>
              </div>

            </div>
          </Modal>
        )}
      />
    );
  }

  scroolContainer: any;

  handleSwitch = (data: FormElementData) => {
    setTimeout(() => {
      if (data.value === true && this.scroolContainer && this.scroolContainer.current) {
        this.scroolContainer.current.scrollTo(0, this.scroolContainer.current.scrollHeight);
      }
    }, 200);
    this.props.handleFormChange(data);
  };

  handleMethodChange = (data: FormElementData) => {
    if (data.value === 'check' || data.value === 'credit_card' || data.value === 'bank_transfer') {
      this.setState({ showInstallments: true });
    } else {
      this.setState({ showInstallments: false });
    }
    this.props.handleFormChange(data);
  };

  handleSwitchInstallments = (data: FormElementData) => {
    this.props.handleFormChange({ name: 'installments', value: data.value ? 2 : 0 });
    this.setState({ installments: data.value ? 2 : 0 });
  };

  // Quantities are checked before the payment's own validation: an empty quantity also leaves the payment at R$ 0,
  // and that error would point at the value instead
  onSave = () => (this.flagInvalidQuantities() ? undefined : this.savePayment());

  flagInvalidQuantities = (): boolean => {
    const { serviceTemplates } = this.props;
    const invalidQuantities = {};
    forEach(this.state.services, (selected, id) => {
      const template = get(serviceTemplates, ['data', String(id)], {});
      if (selected && this.isSoldByQuantity(template) && !ServiceService.isValidQuantity(get(this.state.quantities, String(id), '1'))) {
        invalidQuantities[id] = true;
      }
    });
    if (size(invalidQuantities) === 0) return false;

    const missingQuantityOf = get(serviceTemplates, ['data', Object.keys(invalidQuantities)[0], 'name'], '');
    this.setState({ invalidQuantities, missingQuantityOf }, this.focusInvalidQuantity);
    return true;
  };

  savePayment = () => this.handleValidation(true).then(() => {
    const { onSave, serviceTemplates, formData } = this.props;
    const { services, errors } = this.state;

    const {
      date, method, price, notes, withNotaFiscal, notaFiscalNumber, installments,
    } = formData;

    if (isEmpty(errors)) {
      const selectedServicesIds = [];

      forEach(services, (selected, id) => {
        if (selected) {
          selectedServicesIds.push(id);
        }
      });

      const selectedServices = map(selectedServicesIds, (id: any) => {
        const template = get(serviceTemplates, ['data', String(id)], {});

        if (this.isSoldByQuantity(template)) {
          const quantity = ServiceService.parseQuantity(get(this.state.quantities, String(id), '1'));
          return {
            templateId: template.id,
            price: ServiceService.lineTotal(quantity, template.price),
            quantity,
            unitPrice: parseFloat(template.price),
          };
        }

        return {
          templateId: template.id,
          price: parseFloat(template.price),
        };
      });

      onSave({
        payment: {
          date,
          method,
          price,
          notes,
          withNotaFiscal,
          notaFiscalNumber,
          installments,
        },
        serviceTemplates: filter(selectedServices, item => item.templateId !== null && item.templateId !== undefined),
      });
    }
  });

  // The checkbox and the row click both run on one click, each from the state before it: they agree on the new value
  handleToggleService = (id: number) => (data: FormElementData) => this.setState({
    services: {
      ...this.state.services,
      [id]: get(data, 'value', false),
    },
    ...(get(data, 'value', false) ? {} : this.forgetQuantity(this.state, id)),
  }, this.handleItemTotal);

  handleQuantityChange = (id: number) => (value: string) => this.setState(s => ({
    quantities: { ...s.quantities, [id]: value },
    invalidQuantities: omit(s.invalidQuantities, String(id)),
    missingQuantityOf: null,
  }), this.handleItemTotal);

  forgetQuantity = (s: State, id: number | string) => ({
    quantities: omit(s.quantities, String(id)),
    invalidQuantities: omit(s.invalidQuantities, String(id)),
    missingQuantityOf: null,
  });

  focusInvalidQuantity = () => {
    const input = document.querySelector('#PaymentsFormModal .quantityStepper.invalid input');
    if (!input) return;
    if (input.scrollIntoView) input.scrollIntoView({ block: 'center' });
    input.focus();
  };

  stopRowClick = (event: Object) => event.stopPropagation();

  isSoldByQuantity = (template: Object) => !!this.props.servicesByQuantity && ServiceService.isTemplateByQuantity(template);

  // What a selected template adds to the payment: quantity × price per unit for a template sold by quantity
  templateTotal = (template: Object) => (this.isSoldByQuantity(template)
    ? ServiceService.lineTotal(ServiceService.parseQuantity(get(this.state.quantities, String(template.id), '1')), template.price)
    : parseFloat(template.price));

  handleClickService = (item: Object) => {
    const checked = !get(this.state, ['services', String(item.id)], false);
    this.setState({
      services: { ...this.state.services, [item.id]: checked },
      ...(checked ? {} : this.forgetQuantity(this.state, item.id)),
    }, this.handleItemTotal);
  };

  handleItemTotal = () => {
    const {
      formChange, serviceTemplates, indebt, financialBalance,
    } = this.props;

    const serviceTemplatesData = get(serviceTemplates, 'data', {});

    if (!this.state.isPriceChanged) {
      const selected = filter(serviceTemplatesData, item => get(this.state.services, item.id, false));
      // While a quantity is being typed ("", "1.000."), the payment keeps its last total instead of dropping to R$ 0
      if (selected.some(item => this.isSoldByQuantity(item) && !ServiceService.isValidQuantity(get(this.state.quantities, String(item.id), '1')))) {
        this.handleConfirmation();
        return;
      }
      const selectedServices = map<any, any>(selected, item => ({
        ...item,
        price: this.templateTotal(item),
      }));

      let balance = parseFloat(financialBalance);
      balance = balance > 0 ? balance : balance * -1;

      const total = sumBy(selectedServices, 'price');
      formChange('price', indebt ? balance : total, true).then(this.handleConfirmation);
    } else {
      this.handleConfirmation();
    }
  };

  handleValidation = (isSave: boolean = true): Promise<any> => new Promise((resolve: Function) => {
    const {
      formData, touchedFields,
    } = this.props;

    const _ = createNsTranslator('financial', this.props.t);

    const isTouched = field => touchedFields[field] || isSave;

    const errors = {};
    const parsedPrice = parseFloat(formData.price);

    if (isTouched('price') && (parsedPrice === 0 || isNaN(parsedPrice))) {
      errors.price = _('label/error/valid_value');
    }

    this.handleConfirmation();

    this.setState({ errors: { ...errors } }, resolve);
  });

  handleConfirmation = () => {
    const { onSetConfirmOnClose, formEdited } = this.props;

    const haveSelectedServices = Object.keys(this.state.services).length > 0;

    onSetConfirmOnClose(formEdited() || haveSelectedServices);
  }

  handlePriceChange = (data: Object) => {
    this.setState({ isPriceChanged: true }, () => this.props.handleFormChange(data));
  };

  toggleRenderServices = () => this.setState(s => ({ renderServices: !s.renderServices }));
}


const initialData = {
  date: formatDate(new Date()),
  method: 'money',
  price: null,
};

export default withForm(initialData)(withTranslation()(PaymentFormModal));
